import Phaser from 'phaser';
import * as C from 'tower/constants';
import { asyncCounter, asyncTween, Direction, DIRECTIONS, oppositeDirection, steppedEase } from 'tower/util';
import Maze from 'tower/scenes/battle/maze';
import Enemy from 'tower/scenes/battle/enemy';
import Gel from 'tower/scenes/battle/enemy/gel';
import Item from 'tower/scenes/battle/item';
import { EntityConfig, RoomConfig } from 'tower/types';
import BattleScene, { Floor } from 'tower/scenes/battle';
import gameState from 'tower/state/game-state';
import BladeTrap from 'tower/scenes/battle/enemy/blade';
import MrWall from './enemy/mrwall';
import Knight from './enemy/knight';
import Ghost from './enemy/ghost';
import Player from './player';
import Projectile from './enemy/projectile';
import Wizard from './enemy/wizard';
import Kobold from './enemy/kobold';
import Wisp from './enemy/wisp';
import Statue from './enemy/statue';
import Flame from './flame';
import OldMan from './enemy/oldman';
import Bush from './enemy/bush';
import Centaur from './enemy/centaur';
import Mummy from './enemy/mummy';

/** Body that stores a reference to the room it is associated with. */
export type RoomBody = Phaser.Physics.Arcade.Body & { room: Room };

export default class Room {
  walls!: Phaser.GameObjects.Image;
  floorSprite!: Phaser.GameObjects.TileSprite;
  maze!: Maze;
  doors!: { [key in Direction]: Door | null };
  colliders!: Phaser.Physics.Arcade.Collider[];

  enemies!: Phaser.GameObjects.Group;
  items!: Phaser.GameObjects.Group;
  projectiles!: Phaser.GameObjects.Group;
  interactables!: Phaser.GameObjects.Group;
  misc!: Phaser.GameObjects.Group;

  x: number;
  y: number;
  width = C.ROOM_WIDTH;
  height = C.ROOM_HEIGHT;
  body: RoomBody;

  configCleanup: null | undefined | Function = null;

  /** Whether the room is currently dark logically; controls the "state" of the room and appearence on creation. */
  isDark: boolean;

  /** whether the room _appears_ dark; used for controlling the appearence of darkness for animations without affecting state. */
  visuallyDark: boolean;

  /** Rect for bounds of the room, excluding the walls (no inset, floor only) */
  public rect: Phaser.Geom.Rectangle;

  /** Rect for bounds of the room, inset into the walls for projectiles */
  public wallRect: Phaser.Geom.Rectangle;

  constructor(
    private scene: BattleScene,
    private floor: Floor,
    public roomConfig: RoomConfig,
  ) {
    const { key, floorX, floorY, doorsConfig, serializedLinks, roomItems, dark } = roomConfig;

    this.x = floorX * C.ROOM_WIDTH;
    this.y = floorY * C.ROOM_HEIGHT;
    this.isDark = !!dark;
    this.visuallyDark = !!dark;

    this.rect = new Phaser.Geom.Rectangle(this.x + 32, this.y + 32, this.width - 64, this.height - 64);
    this.wallRect = new Phaser.Geom.Rectangle(this.x + 24, this.y + 24, this.width - 48, this.height - 48);

    // Create maze in constructor so the walls are consistent
    this.maze = Maze.generate(
      scene,
      this.x + 24,
      this.y + 24,
      C.MAZE_WIDTH,
      C.MAZE_HEIGHT,
      doorsConfig,
      serializedLinks,
    );

    // Body used for detecting a room transition; these exist outside of the create/destroy cycle
    this.body = scene.physics.add.body(this.x, this.y, this.width, this.height) as RoomBody;
    this.body.room = this;

    if (roomItems) {
      for (const config of roomItems) {
        gameState.addRoomItem(key, config);
      }
    }

    this.syncOpenedDoors();
    gameState.on('oependdoorschange', () => this.syncOpenedDoors());
  }

  syncOpenedDoors() {
    const openedDoors = gameState.getOpenedDoors(this.roomConfig.key);
    for (const direction of DIRECTIONS) {
      if (this.roomConfig.doorsConfig[direction] === 'locked' && openedDoors[direction]) {
        this.roomConfig.doorsConfig[direction] = 'open';
      }
    }
  }

  create() {
    const { scene, x, y, isDark } = this;
    const { doorsConfig, entityConfigs } = this.roomConfig;

    this.visuallyDark = isDark;
    this.colliders = [];

    // Static level art
    this.walls = scene.add
      .image(x + this.width / 2, y + this.height / 2, isDark ? 'darkWalls' : 'walls')
      .setDepth(C.DEPTHS.Walls);
    this.floorSprite = scene.add
      .tileSprite(x + this.width / 2, y + this.height / 2, this.width, this.height, 'tiles', 1)
      .setDepth(C.DEPTHS.Floor)
      .setTint(isDark ? 0x00000000 : 0xffffffff);

    // Maze walls
    this.maze.create(this.roomConfig.serializedLinks);
    this.maze.syncTilemap();
    this.maze.layer.setDepth(C.DEPTHS.Walls).setTint(isDark ? 0x000000 : 0xffffff);

    // Doors
    this.doors = { up: null, down: null, left: null, right: null };
    for (const direction of DIRECTIONS) {
      if (doorsConfig[direction] !== null) {
        this.doors[direction] = new Door(scene, this, direction, doorsConfig[direction], !!isDark);
      }
    }

    // Enemies
    this.enemies = scene.add.group();
    const playerEnemyCollider = scene.physics.add.overlap(scene.player, this.enemies, (_playerSprite, enemySprite) => {
      const enemy = enemySprite as Enemy;
      if (enemy.touchDamage !== null) {
        scene.player.hit({ weaponBody: enemy.body, damage: enemy.touchDamage, weaponType: enemy.enemyType });
      }
    });
    playerEnemyCollider.name = 'Player enemy damage';
    this.colliders.push(playerEnemyCollider);
    this.colliders.push(
      scene.physics.add.collider(scene.player, this.enemies, undefined, (_playerSprite, enemySprite) => {
        return (enemySprite as Enemy).enemyOptions.solid === true;
      }),
    );
    this.colliders.push(
      scene.physics.add.collider(this.enemies, this.maze.collisionLayer, undefined, (enemyObject, _layer) => {
        return (enemyObject as Enemy).enemyOptions.passthroughWalls !== true;
      }),
    );
    for (const doorObject of Object.values(this.doors)) {
      if (!doorObject) {
        continue;
      }
      const door = doorObject as Door;
      this.colliders.push(
        scene.physics.add.collider(this.enemies, door.doorBody, undefined, (enemyObject, _doorBody) => {
          return !(enemyObject as Enemy).enemyOptions.passthroughDoors;
        }),
      );
    }

    // Items group
    this.items = scene.add.group();
    const itemCollider = scene.physics.add.overlap(
      scene.player,
      this.items,
      (_player, item) => {
        (item as Item).collect();
      },
      (playerObject, _key) => {
        return (playerObject as Player).stateMachine.state !== 'dragging';
      },
    );
    itemCollider.name = 'Item collider';
    this.colliders.push(itemCollider);

    // Interactables
    this.interactables = scene.add.group();
    this.colliders.push(scene.physics.add.collider(scene.player, this.interactables));

    // Misc items that should despawn when this room is destroyed
    this.misc = scene.add.group();

    // Projectiles shot at the player
    this.projectiles = scene.add.group();
    const projectilePlayerCollider = scene.physics.add.overlap(
      scene.player,
      this.projectiles,
      (playerObject, projectileObject) => {
        const player = playerObject as Player;
        const projectile = projectileObject as Projectile;
        player.hit({ weaponBody: projectile.body, damage: projectile.damage, weaponType: projectile.variantKey });
        projectile.destroy(true);
      },
      (_playerObject, projectileObject) => {
        const projectile = projectileObject as Projectile;
        return projectile.options.target === 'player';
      },
    );
    projectilePlayerCollider.name = 'Projectile-player collider';
    this.colliders.push(projectilePlayerCollider);

    // Projectiles shot at enemies
    const projectileEnemyCollider = scene.physics.add.overlap(
      this.enemies,
      this.projectiles,
      (enemyObject, projectileObject) => {
        const enemy = enemyObject as Enemy;
        const projectile = projectileObject as Projectile;
        enemy.hit({ weaponBody: projectile.body, damage: projectile.damage, weaponType: projectile.variantKey });
        projectile.destroy(true);
      },
      (_enemyObject, projectileObject) => {
        const projectile = projectileObject as Projectile;
        return projectile.options.target === 'enemy';
      },
    );
    projectileEnemyCollider.name = 'Projectile-enemy collider';
    this.colliders.push(projectileEnemyCollider);

    // Projectiles optionally can hit walls
    const projectileWallCollider = scene.physics.add.overlap(
      this.maze.layer,
      this.projectiles,
      (projectileObject, tileObject) => {
        const index = (tileObject as Phaser.Tilemaps.Tile).index;
        if (index != -1 && index != 16) {
          (projectileObject as Projectile).handleHitWall();
        }
      },
      (projectileObject, _tileObject) => {
        const projectile = projectileObject as Projectile;
        return projectile.variant.destroyWalls || projectile.variant.hitWalls;
      },
    );
    projectileWallCollider.name = 'Projectile-wall collider';
    this.colliders.push(projectileWallCollider);

    // Spawn entities (temporary enemies + items + other)
    for (const entityConfig of entityConfigs) {
      this.spawnEntity(entityConfig);
    }

    // Spawn room entities (persistent entities stored in the save data, e.g. important items)
    for (const entityConfig of gameState.getRoomItems(this.roomConfig.key)) {
      this.spawnEntity(entityConfig);
    }

    // Spawn new room entities if created
    gameState.on('roomitemchange', this.handleRoomItemChange);

    this.configCleanup = this.roomConfig.onCreate?.(this.scene, this);
  }

  handleFoundKeyChange = (foundKeys: Set<string>) => {
    if (foundKeys.has(this.roomConfig.key)) {
      for (const itemObject of this.items.getChildren()) {
        const item = itemObject as Item;
        if (item.type === 'key') {
          item.destroy();
        }
      }
    }
  };

  handleRoomItemChange = (roomKey: string, roomItems: EntityConfig[]) => {
    if (roomKey !== this.roomConfig.key) {
      return;
    }

    const items = this.items.getChildren() as Item[];

    // Remove any room items that aren't in the list anymore
    for (const item of items) {
      if (item.id !== undefined && !roomItems.find((i) => i.id === item.id)) {
        item.destroy();
      }
    }

    // Add any items that are missing from the list
    for (const entityConfig of roomItems) {
      if (!items.find((i) => i.id === entityConfig.id)) {
        this.spawnEntity(entityConfig);
      }
    }
  };

  /** Transitions the room between light and dark visually, without affecting it's logical state. */
  async transitionDark(newDark: boolean) {
    const { scene } = this;

    if (newDark === this.visuallyDark) {
      return;
    }

    scene.events.emit('pretransitiondark', newDark);

    // Create temporary walls and doors matching the current appearence
    const tempWalls = scene.add
      .image(this.x + this.width / 2, this.y + this.height / 2, this.visuallyDark ? 'darkWalls' : 'walls')
      .setDepth(C.DEPTHS.Walls);
    const tempDoors = Object.values(this.doors)
      .filter((d) => d !== null)
      .map((door) => {
        return new Door(this.scene, this, door.direction, door.state, this.visuallyDark);
      });

    // Convert the real walls and doors to the new dark now that they're covered
    this.walls.setTexture(newDark ? 'darkWalls' : 'walls');
    for (const door of Object.values(this.doors)) {
      if (door) {
        door.dark = newDark;
      }
    }

    // Fade transition
    const duration = 400;
    const fromColor = Phaser.Display.Color.IntegerToColor(newDark ? 0xffffff : 0x000000);
    const toColor = Phaser.Display.Color.IntegerToColor(newDark ? 0x000000 : 0xffffff);
    await Promise.all([
      // Fade the fake walls and doors out to reveal the new ones
      asyncTween(scene, {
        alpha: 0,
        targets: [tempWalls, ...tempDoors.flatMap((d) => [d.ceiling, d.doorway])],
        duration,
        ease: steppedEase(3),
      }),

      // Meanwhile fade the alpha of the hidden-when-dark elements to match the new dark value
      asyncCounter(scene, {
        from: 0,
        to: 1,
        duration,
        ease: steppedEase(3),
        onUpdate: (tween) => {
          const newValue = tween.getValue() ?? 0 * 100;
          const newTint = Phaser.Display.Color.Interpolate.ColorWithColor(fromColor, toColor, 100, newValue * 100);
          this.maze.layer.setTint(newTint.color);
          this.floorSprite.setTint(newTint.color);
        },
      }),
    ]);

    tempWalls.destroy(true);
    for (const door of tempDoors) {
      door.destroy();
    }
    this.visuallyDark = newDark;
  }

  /** Sets the visual appearence of the room without a transition. */
  setVisuallyDark(newDark: boolean) {
    this.walls.setTexture(newDark ? 'darkWalls' : 'walls');
    for (const door of Object.values(this.doors)) {
      if (door) {
        door.dark = newDark;
      }
    }
    this.maze.layer.setTint(newDark ? 0x000000 : 0xffffff);
    this.floorSprite.setTint(newDark ? 0x000000 : 0xffffff);
    this.visuallyDark = newDark;
  }

  spawnEntity(entityConfig: EntityConfig) {
    const { scene } = this;

    let x = entityConfig.x!;
    let y = entityConfig.y!;
    if (entityConfig.spaceX !== undefined && entityConfig.spaceY != undefined) {
      const space = this.maze.spaceXYToWorldXY(entityConfig.spaceX, entityConfig.spaceY);
      x = space[0];
      y = space[1];
    }
    x += entityConfig.offsetX ?? 0;
    y += entityConfig.offsetY ?? 0;

    switch (entityConfig.type) {
      case 'key':
        if (gameState.hasKey(this.roomConfig.key)) {
          return;
        }
        const key = new Item(scene, x, y, 'key', entityConfig.id, this.roomConfig.key);
        this.items.add(key);
        return key;
      case 'mattock':
      case 'boomerang':
      case 'heart1':
      case 'heart2':
      case 'heart3':
      case 'candle':
      case 'betterMattock':
      case 'whiteSword':
      case 'wispRing':
      case 'bow':
      case 'blueTunic':
      case 'betterShield':
      case 'potion':
      case 'blueRod':
      case 'redRod':
      case 'magicKey':
      case 'smallHeart':
        // Silly workaround: If a larger item is being spawned on top of a small heart (or vice versa), get rid of the
        // small heart so that it doesn't, like, hide under the bigger item.
        const underItem = this.items.getChildren().find((i) => (i as Item).x === x && (i as Item).y === y) as
          | Item
          | undefined;
        if (underItem) {
          if (entityConfig.type !== 'smallHeart' && underItem.type === 'smallHeart') {
            underItem.destroy();
          } else if (entityConfig.type === 'smallHeart' && underItem.type !== 'smallHeart') {
            return;
          }
        }

        const item = new Item(scene, x, y, entityConfig.type, entityConfig.id, this.roomConfig.key);
        this.items.add(item);
        return item;
      case 'gel':
        const gel = new Gel(scene, x, y, entityConfig.variant);
        this.enemies.add(gel);
        return gel;
      case 'bladeTrap':
        const bladeTrap = new BladeTrap(scene, x, y, entityConfig.variant);
        this.enemies.add(bladeTrap);
        return bladeTrap;
      case 'mrWall':
        const mrWall = new MrWall(scene, this, x, y, entityConfig.variant);
        this.enemies.add(mrWall);
        return mrWall;
      case 'knight':
        const knight = new Knight(scene, x, y, entityConfig.variant, entityConfig.options);
        this.enemies.add(knight);
        return knight;
      case 'ghost':
        const ghost = new Ghost(scene, x, y, entityConfig.variant, entityConfig.options);
        this.enemies.add(ghost);
        return ghost;
      case 'wizard':
        const wizard = new Wizard(scene, x, y, entityConfig.variant);
        this.enemies.add(wizard);
        return wizard;
      case 'kobold':
        const kobold = new Kobold(scene, x, y, entityConfig.variant);
        this.enemies.add(kobold);
        return kobold;
      case 'mummy':
        const mummy = new Mummy(scene, x, y, entityConfig.variant);
        this.enemies.add(mummy);
        return mummy;
      case 'centaur':
        const centaur = new Centaur(scene, x, y, entityConfig.variant);
        this.enemies.add(centaur);
        return centaur;
      case 'wisp':
        const wisp = new Wisp(scene, x, y, entityConfig.options);
        this.enemies.add(wisp);
        return wisp;
      case 'statue':
        const statue = new Statue(scene, x, y, entityConfig.options);
        this.enemies.add(statue);
        return statue;
      case 'flame':
        const flame = new Flame(scene, this, x, y, entityConfig.options);
        this.misc.add(flame);
        return flame;
      case 'oldman':
        const oldMan = new OldMan(scene, x, y);
        this.enemies.add(oldMan);
        return oldMan;
      case 'bush':
        const bush = new Bush(scene, x, y, entityConfig.options);
        this.enemies.add(bush);
        return bush;
    }
  }

  unlockDoor(direction: Direction) {
    const { doorsConfig, floorX, floorY, key } = this.roomConfig;
    if (doorsConfig[direction] === 'locked' || doorsConfig[direction] === 'closed') {
      doorsConfig[direction] = 'open';
      gameState.setOpenedDoor(key, direction, true);

      const door = this.doors[direction];
      if (door !== null) {
        door.state = 'open';
      }
    }

    const adjacentRoom = this.floor.getAdjacentRoom(floorX, floorY, direction);
    if (adjacentRoom) {
      const adjacentDoorState = adjacentRoom.roomConfig.doorsConfig[oppositeDirection(direction)];
      if (adjacentDoorState === 'locked' || adjacentDoorState === 'closed') {
        adjacentRoom.roomConfig.doorsConfig[oppositeDirection(direction)] = 'open';
        gameState.setOpenedDoor(adjacentRoom.roomConfig.key, oppositeDirection(direction), true);
      }
    }
  }

  destroy() {
    this.configCleanup?.();
    this.walls.destroy();
    this.floorSprite.destroy();
    this.maze.destroy();
    this.enemies.destroy(true, true);
    this.items.destroy(true, true);
    this.misc.destroy(true, true);
    this.interactables.destroy(true, true);
    for (const collider of this.colliders) {
      collider.destroy();
    }
    for (const door of Object.values(this.doors)) {
      door?.destroy();
    }
    gameState.off('roomitemchange', this.handleRoomItemChange);
  }

  update() {
    for (const door of Object.values(this.doors)) {
      if (door !== null) {
        door.update();
      }
    }

    for (const enemy of this.enemies.children) {
      enemy.update();
    }

    for (const gameObject of this.misc.children) {
      gameObject.update();
    }

    for (const projectile of this.projectiles.children) {
      projectile.update();
    }
  }

  lockCamera() {
    this.scene.cameras.main.startFollow(this.scene.player, true).setBounds(this.x, this.y, this.width, this.height);
  }

  unlockCamera() {
    this.scene.cameras.main.stopFollow().removeBounds();
  }

  setVisible(value: boolean) {
    this.walls.setVisible(value);
    this.floorSprite.setVisible(value);
    this.maze.layer.setVisible(value);
    for (const door of Object.values(this.doors)) {
      door?.setVisible(value);
    }
  }

  destroyEntities() {
    for (const enemy of this.enemies.children) {
      enemy.destroy();
    }
    for (const item of this.items.children) {
      item.destroy();
    }
    for (const projectiles of this.projectiles.children) {
      projectiles.destroy();
    }
    for (const misc of this.misc.children) {
      misc.destroy(true);
    }
  }

  static preload(scene: Phaser.Scene) {
    Door.preload(scene);

    scene.load.image('walls', 'img/walls.png');
    scene.load.image('darkWalls', 'img/darkwalls.png');
  }
}

const MAX_DOOR_NOT_COLLIDING_FRAME_COUNT = 5;

type DoorState = 'locked' | 'open' | 'closed' | 'stairs';

class Door {
  ceiling: Phaser.GameObjects.Image;
  doorway: Phaser.GameObjects.Image;
  blockers: Phaser.Physics.Arcade.StaticBody[];
  colliders: Phaser.Physics.Arcade.Collider[];
  doorCollider: Phaser.Physics.Arcade.Collider;
  doorBody: Phaser.Physics.Arcade.StaticBody;

  colliding = false;
  notCollidingFrameCount = 0;
  collisionStart: number | null = null;

  private _state: DoorState;
  private _dark: boolean;

  x: number;
  y: number;

  constructor(
    private scene: BattleScene,
    private room: Room,
    public direction: Direction,
    state: DoorState,
    dark: boolean,
  ) {
    switch (direction) {
      case Direction.Up:
        this.x = room.x + room.width / 2;
        this.y = room.y + 16;
        break;
      case Direction.Right:
        this.x = room.x + room.width - 16;
        this.y = room.y + room.height / 2;
        break;
      case Direction.Down:
        this.x = room.x + room.width / 2;
        this.y = room.y + room.height - 16;
        break;
      case Direction.Left:
        this.x = room.x + 16;
        this.y = room.y + room.height / 2;
        break;
    }

    this.ceiling = scene.add.image(this.x, this.y, 'doors').setDepth(C.DEPTHS.Ceiling);
    this.doorway = scene.add.image(this.x, this.y, 'doors').setDepth(C.DEPTHS.Doorway);

    this.blockers = [];
    switch (direction) {
      case Direction.Up:
      case Direction.Down:
        this.blockers.push(scene.physics.add.staticBody(this.x - 16, this.y - 14, 8, 28));
        this.blockers.push(scene.physics.add.staticBody(this.x + 8, this.y - 14, 8, 28));
        break;
      case Direction.Right:
      case Direction.Left:
        this.blockers.push(scene.physics.add.staticBody(this.x - 14, this.y - 16, 28, 8));
        this.blockers.push(scene.physics.add.staticBody(this.x - 14, this.y + 8, 28, 8));
        break;
    }

    this.colliders = [];
    const blockerCollider = scene.physics.add.collider(scene.player, this.blockers);
    blockerCollider.name = 'Door Blockers';
    this.colliders.push(blockerCollider);

    switch (direction) {
      case Direction.Up:
      case Direction.Down:
        this.doorBody = scene.physics.add.staticBody(this.x - 8, this.y - 14, 16, 28);
        break;
      case Direction.Right:
      case Direction.Left:
        this.doorBody = scene.physics.add.staticBody(this.x - 14, this.y - 8, 28, 16);
        break;
    }

    this.doorCollider = scene.physics.add.collider(scene.player, this.doorBody, () => {
      this.colliding = true;
    });
    this.doorCollider.name = 'Locked door';
    this.colliders.push(this.doorCollider);

    this._dark = dark;
    this._state = state;
    this.sync();
  }

  update() {
    const { scene, room, direction } = this;

    if (!this.colliding && this.notCollidingFrameCount < MAX_DOOR_NOT_COLLIDING_FRAME_COUNT) {
      this.notCollidingFrameCount++;
    } else if (this.colliding) {
      this.notCollidingFrameCount = 0;
    }

    if (this.state === 'locked' || this.state === 'closed') {
      if (this.state === 'locked' && window.location.search.includes('?doordebug')) {
        console.log(`door is locked, player colliding: ${this.colliding}`);
      }
      if (this.state === 'locked' && this.notCollidingFrameCount < MAX_DOOR_NOT_COLLIDING_FRAME_COUNT) {
        if (this.collisionStart === null) {
          this.collisionStart = scene.time.now;
          return;
        } else {
          if (
            scene.time.now - this.collisionStart >= 200 &&
            (gameState.keyCount > 0 || gameState.hasItem('magicKey'))
          ) {
            gameState.useKey();
            this.state = 'open';
            room.unlockDoor(direction); // Store door opening on both sides permanently

            scene.sound.play('openDoor');
          }
        }
      } else {
        this.collisionStart = null;
      }
    }

    this.colliding = false;
  }

  get state() {
    return this._state;
  }

  set state(value: DoorState) {
    this._state = value;
    this.sync();
  }

  get dark() {
    return this._dark;
  }

  set dark(value: boolean) {
    this._dark = value;
    this.sync();
  }

  sync() {
    let spriteIndex = 0;
    switch (this.direction) {
      case Direction.Up:
        spriteIndex = 0;
        break;
      case Direction.Right:
        spriteIndex = 2;
        break;
      case Direction.Down:
        spriteIndex = 4;
        break;
      case Direction.Left:
        spriteIndex = 6;
        break;
    }

    let doorStateMod = 0;
    if (this.state === 'locked') {
      doorStateMod = 8;
    } else if (this.state === 'closed') {
      doorStateMod = 16;
    } else if (this.state === 'stairs') {
      doorStateMod = 24;
    }
    const darkMod = this.dark ? 32 : 0;
    this.ceiling.setFrame(spriteIndex + darkMod);
    this.doorway.setFrame(spriteIndex + 1 + doorStateMod + darkMod);

    this.doorCollider.active = this.state === 'closed' || this.state === 'locked';
  }

  setVisible(value: boolean) {
    this.ceiling.setVisible(value);
    this.doorway.setVisible(value);
  }

  destroy() {
    this.ceiling.destroy();
    this.doorway.destroy();
    this.doorBody?.destroy();
    for (const collider of this.colliders) {
      collider.destroy();
    }
    for (const blocker of this.blockers) {
      blocker.destroy();
    }
  }

  static preload(scene: Phaser.Scene) {
    scene.load.spritesheet('doors', 'img/doors.png', { frameWidth: 32, frameHeight: 32 });
    scene.load.audio('openDoor', 'audio/door.mp3');
  }
}
